import axios from 'axios'
import {
  MOBILE_PURCHASE_API,
  PRIVATE_TOKEN_URL,
  PRIVATE_CLIENT_ID,
} from './config'

// A bare axios instance — NOT the shared axiosInstance, whose interceptor
// injects the company-realm (userManager) token. Here we attach the private
// user's token explicitly per call.
const http = axios.create({ timeout: 30000 })

export interface MobilePrice {
  priceGrosze: number
  currency: string
}

export interface MobileStatus {
  paid: boolean
  paymentDate: string | null
}

export interface MobileCheckout {
  url: string
  sessionId: string
}

export interface MobileConfirm {
  activated: boolean
  pending: boolean
  failed: boolean
}

// Invoice details for the buyer. Collected here because Przelewy24 is not a
// source of them: name/address are merchant-supplied fields it only echoes back,
// and BLIK/card notifications carry no payer identity at all. Mobile app buyers
// are always private individuals — no company name, no NIP.
export interface MobileInvoiceBuyer {
  name: string
  street: string
  buildingNo: string
  apartmentNo: string
  postcode: string
  city: string
  country: string
  email: string
}

const authHeader = (token: string) => ({ Authorization: `Bearer ${token}` })

// Direct grant against the private realm (fallback browser login / testing).
export async function loginPrivate(username: string, password: string): Promise<string> {
  const body = new URLSearchParams()
  body.set('grant_type', 'password')
  body.set('client_id', PRIVATE_CLIENT_ID)
  body.set('username', username)
  body.set('password', password)
  const { data } = await http.post(PRIVATE_TOKEN_URL, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  return data.access_token as string
}

export async function getPrice(): Promise<MobilePrice> {
  const { data } = await http.get(`${MOBILE_PURCHASE_API}/price`)
  return data
}

export async function getStatus(token: string): Promise<MobileStatus> {
  const { data } = await http.get(`${MOBILE_PURCHASE_API}/status`, { headers: authHeader(token) })
  return data
}

export async function createCheckout(
  token: string,
  buyer: MobileInvoiceBuyer,
  returnUrl?: string | null,
): Promise<MobileCheckout> {
  const { data } = await http.post(
    `${MOBILE_PURCHASE_API}/checkout`,
    { returnUrl: returnUrl ?? null, buyer },
    { headers: { ...authHeader(token), 'Content-Type': 'application/json' } },
  )
  return data
}

export async function confirmCheckout(token: string, sessionId: string): Promise<MobileConfirm> {
  const { data } = await http.post(
    `${MOBILE_PURCHASE_API}/confirm`,
    { sessionId },
    { headers: { ...authHeader(token), 'Content-Type': 'application/json' } },
  )
  return data
}

// Email claim of the private user's token, used to prefill the invoice form.
// The backend re-reads the claim itself and only falls back to it, so a garbled
// token here costs a prefill, never correctness.
export function readEmailFromToken(token: string): string | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as { email?: string }
    return claims.email ?? null
  } catch {
    return null
  }
}

// Whether the token's `exp` claim has passed (with a small margin for clock skew
// and request latency). The app forwards a short-lived access token (5 min in the
// private realm), so a user who takes longer to fill in the invoice form submits
// an expired one — catching it here shows a clear "session expired" message
// instead of a generic failure. An unreadable token counts as expired.
export function isTokenExpired(token: string, nowMs: number = Date.now(), skewSeconds = 30): boolean {
  try {
    const payload = token.split('.')[1]
    if (!payload) return true
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const { exp } = JSON.parse(json) as { exp?: number }
    if (typeof exp !== 'number') return true
    return exp - skewSeconds <= nowMs / 1000
  } catch {
    return true
  }
}

// Read an access token forwarded by the mobile app via the URL hash
// (#access_token=...), so a user already logged in to the app isn't asked to
// log in again on the web payment page.
export function readTokenFromHash(): string | null {
  if (typeof window === 'undefined' || !window.location.hash) return null
  const hash = window.location.hash.replace(/^#/, '')
  const params = new URLSearchParams(hash)
  return params.get('access_token')
}
